// Leaf imports, not the `../data-table` barrel: the barrel pulls in components that read `window`
// at import time, which keeps these helpers out of the DOM-free test config.
import { DEFAULT_MAX_CLAIMS, rateLimitedRequest } from '../../utils/collect-by-claim-ids';
import type { RateLimiter } from '../../utils/rate-limiter';
import { FilterMethod } from '../data-table/components/filters/filters.models';
import { parseFilter } from '../data-table/data-table.service';

export const SEARCH_ID_CAP = 100;
const COMMENT_PAGE_SIZE = 100;

/** Where the task-list search box looks. Exactly one at a time — see the search popover. */
export type TaskSearchIn = 'subject' | 'body' | 'coding' | 'rule';

/**
 * A coder rule number as an operator types it: digits, optionally after a `#`. Anything else is
 * `null`, never a guess — `GET /claims` takes the number raw (`filterCodingRuleNumber=12`), and the
 * `EQ:12` form the other filters use answers 500 (measured on sandbox 2026-10-01).
 */
export function parseCoderRuleNumber(input: string): number | null {
    const match = input.trim().match(/^#?\s*(\d{1,9})$/);
    return match ? Number(match[1]) : null;
}

/**
 * Build the FTS:… value for a full-text filter (same path as mail OCR FullText).
 * Uses CT (contains all words) so multi-word queries require each token.
 */
function buildFtsValue(filterId: string, query: string): string {
    const trimmed = query.trim();
    if (!trimmed) {
        return '';
    }
    return parseFilter({
        filterId,
        filterType: 'FullText',
        filterMethod: FilterMethod.CT,
        value: trimmed,
    });
}

/** FTS value for `filterBody` on GET /comment. */
export function buildCommentBodyFtsValue(query: string): string {
    return buildFtsValue('filterBody', query);
}

/** FTS value for `filterResult` on GET /coding. */
export function buildCodingResultFtsValue(query: string): string {
    return buildFtsValue('filterResult', query);
}

export interface ICommentSearchHit {
    messageId?: string;
}

export interface ICodingSearchHit {
    id?: string;
    claimId?: string;
}

/**
 * Sentinel list filter for the claim DOS range — never sent to GET /message, which has no DOS
 * filter and ignores one silently (measured 2026-10-06, provider and support: every DOS param name
 * returned the unfiltered total). Answered through the claims it selects — see
 * `claim-scoped-task-listing.ts` (CA-3441). Stored as up to two `Date` filters: GTE = from, LTE = to.
 */
export const CLAIM_DOS_FILTER_ID = 'filterClaimFromDos';

export interface IClaimDosRange {
    from?: Date;
    to?: Date;
}

/** The DOS range held in the list filters; either end may be absent. */
export function readClaimDosRange(filters?: Array<{ filterId?: string; filterMethod?: string; value?: unknown }>): IClaimDosRange {
    const range: IClaimDosRange = {};
    for (const filter of filters || []) {
        if (filter.filterId !== CLAIM_DOS_FILTER_ID || !(filter.value instanceof Date)) {
            continue;
        }
        if (filter.filterMethod === FilterMethod.GTE) {
            range.from = filter.value;
        } else if (filter.filterMethod === FilterMethod.LTE) {
            range.to = filter.value;
        }
    }
    return range;
}

/**
 * `filterFromDos` value for GET /claims: both ends → `BT:from:to`, one end → `GTE:` / `LTE:`,
 * neither → ''. All three are inclusive (measured on sandbox 2026-10-06: GTE d + LTE d = total +
 * the claims on d). It matches the claim's FIRST date of service only.
 */
export function buildClaimFromDosValue(range: IClaimDosRange): string {
    if (range.from && range.to) {
        return parseFilter({
            filterId: 'filterFromDos',
            filterType: 'DateRange',
            filterMethod: FilterMethod.BT,
            value: [range.from, range.to],
        });
    }
    const single = range.from || range.to;
    if (!single) {
        return '';
    }
    return parseFilter({
        filterId: 'filterFromDos',
        filterType: 'Date',
        filterMethod: range.from ? FilterMethod.GTE : FilterMethod.LTE,
        value: single,
    });
}

/**
 * Page through a list endpoint and collect unique IDs (capped).
 *
 * For the Body search the cap is the ceiling of "search a child resource, then filter tasks by
 * the IDs it returns": those message IDs travel in the query string, so it cannot be raised far.
 * The Coding result's claim IDs do not — they are answered through claim chunks
 * (claim-scoped-task-listing.ts), so its cap is the claim collection's, not the URL's.
 *
 * With `rateLimiter`, every page waits for that budget and a 429 pauses and retries
 * (`rateLimitedRequest`) — a long walk is otherwise an unthrottled burst.
 *
 * `signal` stops the walk between pages (a page already in flight still lands, then nothing more
 * is requested). A superseded search must stop: a 5,000-claim walk left running would keep
 * spending the budget the current search waits on.
 */
async function collectUniqueIds<THit>(options: {
    fetchPage: (args: { limit: number; offset: number }) => Promise<THit[]>;
    readId: (hit: THit) => string | undefined;
    cap?: number;
    rateLimiter?: RateLimiter;
    signal?: AbortSignal;
}): Promise<string[]> {
    const cap = options.cap ?? SEARCH_ID_CAP;
    const { rateLimiter, signal } = options;
    const ids = new Set<string>();
    let offset = 0;

    while (ids.size < cap) {
        if (signal?.aborted) {
            throw new DOMException('Search superseded', 'AbortError');
        }
        const args = { limit: COMMENT_PAGE_SIZE, offset };
        const page = await (rateLimiter
            ? rateLimitedRequest(() => options.fetchPage(args), rateLimiter, signal)
            : options.fetchPage(args));
        if (!page.length) {
            break;
        }
        for (const hit of page) {
            const id = options.readId(hit);
            if (id) {
                ids.add(id);
                if (ids.size >= cap) {
                    break;
                }
            }
        }
        if (page.length < COMMENT_PAGE_SIZE) {
            break;
        }
        offset += COMMENT_PAGE_SIZE;
    }

    return [...ids];
}

/** {@link collectUniqueIds} over an FTS filter value; an empty value searches nothing. */
async function collectIdsFromFtsSearch<THit>(options: {
    ftsValue: string;
    fetchPage: (args: { fts: string; limit: number; offset: number }) => Promise<THit[]>;
    readId: (hit: THit) => string | undefined;
    cap?: number;
    rateLimiter?: RateLimiter;
    signal?: AbortSignal;
}): Promise<string[]> {
    if (!options.ftsValue) {
        return [];
    }
    return collectUniqueIds({
        fetchPage: ({ limit, offset }) => options.fetchPage({ fts: options.ftsValue, limit, offset }),
        readId: options.readId,
        cap: options.cap,
        rateLimiter: options.rateLimiter,
        signal: options.signal,
    });
}

/**
 * Page through comment FTS results and collect unique message IDs (capped).
 */
export async function collectMessageIdsFromCommentSearch(options: {
    query: string;
    searchComments: (args: {
        filterBody: string[];
        limit: number;
        offset: number;
    }) => Promise<ICommentSearchHit[]>;
    cap?: number;
    signal?: AbortSignal;
}): Promise<string[]> {
    return collectIdsFromFtsSearch({
        ftsValue: buildCommentBodyFtsValue(options.query),
        fetchPage: ({ fts, limit, offset }) =>
            options.searchComments({ filterBody: [fts], limit, offset }),
        readId: hit => hit.messageId,
        cap: options.cap,
        signal: options.signal,
    });
}

/**
 * Page through coding FTS results and collect the unique CLAIM IDs they belong to — up to
 * {@link DEFAULT_MAX_CLAIMS}, the same ceiling as any other claim-scoped task filter.
 *
 * Claim, not coding: measured 2026-09-15 on a live account, coding-problem tasks carry a `claims`
 * link (50/50 sampled) and never a `codings` link (0/50), so `filterCodingId` on GET /message has
 * nothing to match. Every coding carries `claimId`; the tasks on those claims are then collected
 * 100 claims at a time (claim-scoped-task-listing.ts), never in one URL.
 */
export async function collectClaimIdsFromCodingSearch(options: {
    query: string;
    searchCodings: (args: {
        filterResult: string[];
        limit: number;
        offset: number;
    }) => Promise<ICodingSearchHit[]>;
    cap?: number;
    rateLimiter?: RateLimiter;
    signal?: AbortSignal;
}): Promise<string[]> {
    return collectIdsFromFtsSearch({
        ftsValue: buildCodingResultFtsValue(options.query),
        fetchPage: ({ fts, limit, offset }) =>
            options.searchCodings({ filterResult: [fts], limit, offset }),
        readId: hit => hit.claimId,
        cap: options.cap ?? DEFAULT_MAX_CLAIMS,
        rateLimiter: options.rateLimiter,
        signal: options.signal,
    });
}

/**
 * One query-keyed, single-flight cache of search → IDs.
 *
 * Shared by the comment and coding searches on purpose: the supersede logic below (ignore a
 * resolution once a newer query has replaced it) is the part that goes subtly wrong when copied.
 *
 * A run owns the cache until a newer query or `clear` replaces it. Replacing it ABORTS the run
 * (its walk stops between pages and its promise rejects with an AbortError, which the task list
 * already ignores) rather than only forgetting it. `onOwned` runs only while the run still owns
 * the cache — anything published alongside the IDs (the cap summary) must not be written by a
 * run that finished after a newer one.
 */
function createSearchIdCache(): {
    get: (term: string, run: (signal: AbortSignal) => Promise<string[]>, onOwned?: (ids: string[]) => void) => Promise<string[]>;
    clear: () => void;
} {
    let cacheQuery: string | null = null;
    let cacheIds: string[] | null = null;
    let inflight: Promise<string[]> | null = null;
    let controller: AbortController | null = null;

    const clear = (): void => {
        controller?.abort();
        controller = null;
        cacheQuery = null;
        cacheIds = null;
        inflight = null;
    };

    return {
        clear,
        get: (term, run, onOwned) => {
            if (cacheQuery === term && cacheIds) {
                return Promise.resolve(cacheIds);
            }
            if (cacheQuery === term && inflight) {
                return inflight;
            }

            controller?.abort();
            const own = new AbortController();
            controller = own;
            cacheQuery = term;
            cacheIds = null;
            let pending: Promise<string[]>;
            const owns = (): boolean => cacheQuery === term && inflight === pending && controller === own;
            pending = run(own.signal).then(
                ids => {
                    // Ignore writes after clear or a newer query superseded this request.
                    if (owns()) {
                        cacheIds = ids;
                        inflight = null;
                        onOwned?.(ids);
                    }
                    return ids;
                },
                error => {
                    if (owns()) {
                        inflight = null;
                    }
                    throw error;
                },
            );
            inflight = pending;

            return pending;
        },
    };
}

const commentFtsCache = createSearchIdCache();
const codingFtsCache = createSearchIdCache();

export interface ICodingSearchSummary {
    query: string;
    claimCount: number;
    /** The claim list stopped at the cap, so the tasks shown are not all of the term's tasks. */
    capped: boolean;
}

let codingSummary: ICodingSearchSummary | null = null;

/**
 * What the last resolved Coding result search found — read by the search bar to say when the
 * results stop at the cap. Silent truncation would read as "these are all the matches".
 */
export function getCodingSearchSummary(): ICodingSearchSummary | null {
    return codingSummary;
}

/** Drop every cached search → ID mapping. Call when the search input text changes. */
export function clearTaskSearchIdCaches(): void {
    commentFtsCache.clear();
    codingFtsCache.clear();
    codingSummary = null;
}

/**
 * Return cached comment FTS message IDs for `query`, or run FTS once and cache.
 * Call {@link clearTaskSearchIdCaches} when the search input text changes.
 */
export async function getCachedCommentFtsMessageIds(options: {
    query: string;
    searchComments: (args: {
        filterBody: string[];
        limit: number;
        offset: number;
    }) => Promise<ICommentSearchHit[]>;
    cap?: number;
}): Promise<string[]> {
    const term = options.query.trim();
    if (!term) {
        clearTaskSearchIdCaches();
        return [];
    }

    return commentFtsCache.get(term, signal =>
        collectMessageIdsFromCommentSearch({
            query: term,
            searchComments: options.searchComments,
            cap: options.cap,
            signal,
        }),
    );
}

/**
 * Return cached claim IDs for a coding-result `query`, or run FTS once and cache.
 * Call {@link clearTaskSearchIdCaches} when the search input text changes.
 */
export async function getCachedCodingFtsClaimIds(options: {
    query: string;
    searchCodings: (args: {
        filterResult: string[];
        limit: number;
        offset: number;
    }) => Promise<ICodingSearchHit[]>;
    cap?: number;
    rateLimiter?: RateLimiter;
}): Promise<string[]> {
    const term = options.query.trim();
    if (!term) {
        clearTaskSearchIdCaches();
        return [];
    }

    const cap = options.cap ?? DEFAULT_MAX_CLAIMS;
    return codingFtsCache.get(
        term,
        signal =>
            collectClaimIdsFromCodingSearch({
                query: term,
                searchCodings: options.searchCodings,
                cap,
                rateLimiter: options.rateLimiter,
                signal,
            }),
        // Only the run that still owns the cache may say whether the list was cut.
        ids => {
            codingSummary = { query: term, claimCount: ids.length, capped: ids.length >= cap };
        },
    );
}
